// Main JavaScript entry point
import '../css/main.css';

// Example: Initialize any JavaScript functionality
document.addEventListener('DOMContentLoaded', () => {
  console.log('Nistplatz initialized');

  // Dark mode toggle functionality (if you add a toggle button)
  initDarkMode();
  
  // Initialize Veranstaltungen filter
  initVeranstaltungenFilter();
  
  // Initialize table row clicks
  initTableRowClicks();

  // Initialize progressive row reveal for veranstaltungen tables
  initVeranstaltungenTableLoadMore();
  
  // Initialize accordion
  initAccordion();

  // Re-animate titles when they scroll back into view
  initTitleAppearanceOnScroll();

  // Animate table rows on mobile scroll
  initMobileTableRowAnimations();

  // Initialize desktop nav submenu behavior
  initNavSubmenus();

  // Initialize mobile nav drawer behavior
  initMobileNavDrawer();

  // Initialize Showcase block carousel
  initShowcaseCarousel();

  // Initialize Event Highlights block carousel
  initEventHighlightsCarousel();

  // Initialize Gallery block carousel
  initGalleryCarousel();

  // Open external links in a new tab
  initExternalLinks();

});

const DRAG_INTERACTIVE_SELECTOR = 'a, button, input, select, textarea, [role="button"], [data-no-drag]';

const VT_MOBILE_ROWS_RESYNC_EVENT = 'vt-mobile-rows-resync';
let mobileTableRowAnimationsBound = false;

function requestMobileRowsResync() {
  document.dispatchEvent(new Event(VT_MOBILE_ROWS_RESYNC_EVENT));
}

function initExternalLinks() {
  const currentHost = window.location.hostname;
  document.querySelectorAll('a[href]').forEach((link) => {
    const href = link.getAttribute('href');
    if (!href) {
      return;
    }
    let url;
    try {
      url = new URL(href, window.location.href);
    } catch {
      return;
    }
    if (url.hostname && url.hostname !== currentHost) {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    }
  });
}

function initShowcaseCarousel() {
  const carousels = Array.from(document.querySelectorAll('[data-showcase-carousel]'));

  if (carousels.length === 0) {
    return;
  }

  carousels.forEach((carousel) => {
    if (!(carousel instanceof HTMLElement)) {
      return;
    }

    const viewport = carousel.querySelector('[data-showcase-viewport]');
    const track = carousel.querySelector('[data-showcase-track]');
    const prevButton = carousel.querySelector('[data-showcase-prev]');
    const nextButton = carousel.querySelector('[data-showcase-next]');
    const dotsContainer = carousel.querySelector('[data-showcase-dots]');
    const slides = Array.from(carousel.querySelectorAll('[data-showcase-slide]'));

    if (!(viewport instanceof HTMLElement) || !(track instanceof HTMLElement) || slides.length <= 1) {
      return;
    }

    const autoplayEnabled = carousel.dataset.showcaseAutoplay === 'true';
    const autoplayDelayRaw = Number.parseInt(carousel.dataset.showcaseDelay ?? '6500', 10);
    const autoplayDelay = Number.isFinite(autoplayDelayRaw)
      ? Math.max(2000, Math.min(20000, autoplayDelayRaw))
      : 6500;

    let currentIndex = 0;
    let autoplayTimerId = null;
    let isPaused = false;
    let pointerStartX = 0;
    let pointerCurrentX = 0;
    let isDragging = false;
    let wasPointerMoved = false;
    const dragIntentThreshold = 10;

    track.style.transform = 'translate3d(0, 0, 0)';

    const dots = slides.map((_, index) => {
      if (!(dotsContainer instanceof HTMLElement)) {
        return null;
      }

      const dotButton = document.createElement('button');
      dotButton.type = 'button';
      dotButton.className = 'showcase-carousel__dot';
      dotButton.setAttribute('aria-label', `Gehe zu Slide ${index + 1}`);
      dotButton.addEventListener('click', () => {
        goTo(index);
      });
      dotsContainer.appendChild(dotButton);

      return dotButton;
    });

    const setSlideA11y = () => {
      slides.forEach((slide, index) => {
        slide.setAttribute('aria-hidden', index === currentIndex ? 'false' : 'true');
      });
    };

    const setDotsState = () => {
      dots.forEach((dotButton, index) => {
        if (!(dotButton instanceof HTMLButtonElement)) {
          return;
        }

        const isActive = index === currentIndex;
        dotButton.classList.toggle('is-active', isActive);
        dotButton.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    };

    const goTo = (index, options = {}) => {
      const { immediate = false } = options;
      const boundedIndex = ((index % slides.length) + slides.length) % slides.length;
      currentIndex = boundedIndex;

      track.style.transition = immediate ? 'none' : '';
      track.style.transform = `translate3d(${-100 * currentIndex}%, 0, 0)`;
      setSlideA11y();
      setDotsState();

      if (immediate) {
        window.requestAnimationFrame(() => {
          track.style.transition = '';
        });
      }
    };

    const stopAutoplay = () => {
      if (autoplayTimerId !== null) {
        window.clearInterval(autoplayTimerId);
        autoplayTimerId = null;
      }
    };

    const startAutoplay = () => {
      if (!autoplayEnabled || isPaused || slides.length <= 1 || autoplayTimerId !== null) {
        return;
      }

      autoplayTimerId = window.setInterval(() => {
        goTo(currentIndex + 1);
      }, autoplayDelay);
    };

    const pauseAutoplay = () => {
      isPaused = true;
      stopAutoplay();
    };

    const resumeAutoplay = () => {
      isPaused = false;
      startAutoplay();
    };

    const onPointerDown = (event) => {
      if (!event.isPrimary || event.button !== 0) {
        return;
      }

      if (event.target instanceof Element && event.target.closest(DRAG_INTERACTIVE_SELECTOR)) {
        return;
      }

      isDragging = true;
      wasPointerMoved = false;
      pointerStartX = event.clientX;
      pointerCurrentX = event.clientX;
      pauseAutoplay();

      track.style.transition = 'none';
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerUp);
    };

    const onPointerMove = (event) => {
      if (!isDragging) {
        return;
      }

      pointerCurrentX = event.clientX;
      const deltaX = pointerCurrentX - pointerStartX;
      const viewportWidth = viewport.clientWidth;

      if (Math.abs(deltaX) > dragIntentThreshold) {
        wasPointerMoved = true;
      }

      if (wasPointerMoved && viewportWidth > 0) {
        const dragPercent = (deltaX / viewportWidth) * 100;
        track.style.transform = `translate3d(${(-100 * currentIndex) + dragPercent}%, 0, 0)`;
      }
    };

    const onPointerUp = (event) => {
      if (!isDragging) {
        return;
      }

      isDragging = false;
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      track.style.transition = '';

      const viewportWidth = viewport.clientWidth;
      const deltaX = pointerCurrentX - pointerStartX;
      const threshold = viewportWidth * 0.12;

      if (Math.abs(deltaX) >= threshold) {
        goTo(deltaX < 0 ? currentIndex + 1 : currentIndex - 1);
      } else {
        goTo(currentIndex);
      }

      resumeAutoplay();
    };

    prevButton?.addEventListener('click', () => {
      goTo(currentIndex - 1);
    });

    nextButton?.addEventListener('click', () => {
      goTo(currentIndex + 1);
    });

    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(currentIndex + 1);
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(currentIndex - 1);
      }
    });

    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('mouseenter', () => {
      if (!isDragging) {
        pauseAutoplay();
      }
    });
    viewport.addEventListener('mouseleave', () => {
      resumeAutoplay();
    });

    viewport.addEventListener('click', (event) => {
      if (wasPointerMoved) {
        event.preventDefault();
        event.stopPropagation();
      }

      wasPointerMoved = false;
    }, true);

    carousel.addEventListener('focusin', pauseAutoplay);
    carousel.addEventListener('focusout', (event) => {
      if (!(event.relatedTarget instanceof Node) || !carousel.contains(event.relatedTarget)) {
        resumeAutoplay();
      }
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stopAutoplay();
      } else {
        startAutoplay();
      }
    });

    goTo(0, { immediate: true });
    startAutoplay();
  });
}

function initEventHighlightsCarousel() {
  const carousels = Array.from(document.querySelectorAll('[data-event-highlights-carousel]'));

  if (carousels.length === 0) {
    return;
  }

  carousels.forEach((carousel) => {
    if (!(carousel instanceof HTMLElement)) {
      return;
    }

    const viewport = carousel.querySelector('[data-event-highlights-viewport]');
    const track = carousel.querySelector('[data-event-highlights-track]');
    const prevButton = carousel.querySelector('[data-event-highlights-prev]');
    const nextButton = carousel.querySelector('[data-event-highlights-next]');
    const dotsContainer = carousel.querySelector('[data-event-highlights-dots]');
    const slides = Array.from(carousel.querySelectorAll('[data-event-highlights-slide]'));

    if (!(viewport instanceof HTMLElement) || !(track instanceof HTMLElement) || slides.length <= 1) {
      return;
    }

    let currentIndex = 0;
    let visibleSlides = 1;
    let maxIndex = 0;
    let slideWidth = 0;
    let trackGap = 0;
    let pointerStartX = 0;
    let pointerCurrentX = 0;
    let isDragging = false;
    let wasPointerMoved = false;
    let dots = [];
    const dragIntentThreshold = 10;

    const getVisibleSlides = () => {
      const viewportWidth = viewport.clientWidth;

      if (viewportWidth >= 1400) {
        return Math.min(3, slides.length);
      }

      if (viewportWidth >= 768) {
        return Math.min(2, slides.length);
      }

      return 1;
    };

    const setSlideA11y = () => {
      slides.forEach((slide, index) => {
        const visibleStart = currentIndex;
        const visibleEnd = currentIndex + visibleSlides - 1;
        const isVisible = index >= visibleStart && index <= visibleEnd;
        slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
      });
    };

    const setDotsState = () => {
      dots.forEach((dotButton, index) => {
        if (!(dotButton instanceof HTMLButtonElement)) {
          return;
        }

        const isActive = index === currentIndex;
        dotButton.classList.toggle('is-active', isActive);
        dotButton.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    };

    const updateTransform = (offsetX = 0, immediate = false) => {
      if (immediate) {
        track.style.transition = 'none';
      } else {
        track.style.transition = '';
      }

      const translateX = (currentIndex * (slideWidth + trackGap) * -1) + offsetX;
      track.style.transform = `translate3d(${translateX}px, 0, 0)`;

      if (immediate) {
        window.requestAnimationFrame(() => {
          track.style.transition = '';
        });
      }
    };

    const rebuildDots = () => {
      if (!(dotsContainer instanceof HTMLElement)) {
        dots = [];
        return;
      }

      dotsContainer.innerHTML = '';
      dots = [];

      for (let index = 0; index <= maxIndex; index += 1) {
        const dotButton = document.createElement('button');
        dotButton.type = 'button';
        dotButton.className = 'showcase-carousel__dot event-highlights-carousel__dot';
        dotButton.setAttribute('aria-label', `Gehe zu Slide ${index + 1}`);
        dotButton.addEventListener('click', () => {
          goTo(index);
        });
        dotsContainer.appendChild(dotButton);
        dots.push(dotButton);
      }
    };

    const syncDimensions = () => {
      visibleSlides = getVisibleSlides();
      maxIndex = Math.max(0, slides.length - visibleSlides);

      if (currentIndex > maxIndex) {
        currentIndex = maxIndex;
      }

      carousel.style.setProperty('--event-highlights-visible', String(visibleSlides));

      const firstSlide = slides[0];
      if (firstSlide instanceof HTMLElement) {
        slideWidth = firstSlide.getBoundingClientRect().width;
      }

      const trackStyles = window.getComputedStyle(track);
      trackGap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap || '0');
      if (!Number.isFinite(trackGap)) {
        trackGap = 0;
      }

      rebuildDots();
      updateTransform(0, true);
      setSlideA11y();
      setDotsState();
    };

    const goTo = (index) => {
      if (maxIndex <= 0) {
        currentIndex = 0;
      } else {
        const normalized = ((index % (maxIndex + 1)) + (maxIndex + 1)) % (maxIndex + 1);
        currentIndex = normalized;
      }

      updateTransform();
      setSlideA11y();
      setDotsState();
    };

    const onPointerDown = (event) => {
      if (!event.isPrimary || event.button !== 0) {
        return;
      }

      if (event.target instanceof Element && event.target.closest(DRAG_INTERACTIVE_SELECTOR)) {
        return;
      }

      isDragging = true;
      wasPointerMoved = false;
      pointerStartX = event.clientX;
      pointerCurrentX = event.clientX;
      track.style.transition = 'none';
      viewport.setPointerCapture(event.pointerId);
    };

    const onPointerMove = (event) => {
      if (!isDragging) {
        return;
      }

      pointerCurrentX = event.clientX;
      const deltaX = pointerCurrentX - pointerStartX;

      if (Math.abs(deltaX) > dragIntentThreshold) {
        wasPointerMoved = true;
      }

      if (wasPointerMoved) {
        updateTransform(deltaX, true);
      }
    };

    const onPointerUp = (event) => {
      if (!isDragging) {
        return;
      }

      isDragging = false;
      if (viewport.hasPointerCapture(event.pointerId)) {
        viewport.releasePointerCapture(event.pointerId);
      }
      track.style.transition = '';

      const deltaX = pointerCurrentX - pointerStartX;
      const threshold = Math.max(40, slideWidth * 0.12);

      if (Math.abs(deltaX) >= threshold) {
        goTo(deltaX < 0 ? currentIndex + 1 : currentIndex - 1);
      } else {
        goTo(currentIndex);
      }
    };

    prevButton?.addEventListener('click', () => {
      goTo(currentIndex - 1);
    });

    nextButton?.addEventListener('click', () => {
      goTo(currentIndex + 1);
    });

    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(currentIndex + 1);
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(currentIndex - 1);
      }
    });

    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove);
    viewport.addEventListener('pointerup', onPointerUp);
    viewport.addEventListener('pointercancel', onPointerUp);

    viewport.addEventListener('click', (event) => {
      if (wasPointerMoved) {
        event.preventDefault();
        event.stopPropagation();
      }

      wasPointerMoved = false;
    }, true);

    let resizeTimer = null;
    window.addEventListener('resize', () => {
      if (resizeTimer !== null) {
        window.clearTimeout(resizeTimer);
      }

      resizeTimer = window.setTimeout(() => {
        syncDimensions();
        resizeTimer = null;
      }, 100);
    });

    syncDimensions();
  });
}

function initGalleryCarousel() {
  const carousels = Array.from(document.querySelectorAll('[data-gallery-carousel]'));

  if (carousels.length === 0) {
    return;
  }

  // Share of a slide width that the next image peeks in by.
  const PEEK_RATIO = 0.15;
  const MIN_SLIDE_HEIGHT = 160;
  const MAX_SLIDE_HEIGHT = 640;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  carousels.forEach((carousel) => {
    if (!(carousel instanceof HTMLElement)) {
      return;
    }

    const viewport = carousel.querySelector('[data-gallery-viewport]');
    const track = carousel.querySelector('[data-gallery-track]');
    const prevButton = carousel.querySelector('[data-gallery-prev]');
    const nextButton = carousel.querySelector('[data-gallery-next]');
    const dotsContainer = carousel.querySelector('[data-gallery-dots]');
    const slides = Array.from(carousel.querySelectorAll('[data-gallery-slide]'));

    if (!(viewport instanceof HTMLElement) || !(track instanceof HTMLElement) || slides.length <= 1) {
      return;
    }

    const isCropped = carousel.dataset.crop === 'true';
    const cropRatioRaw = Number.parseFloat(carousel.dataset.galleryRatio ?? '');
    const cropRatio = Number.isFinite(cropRatioRaw) && cropRatioRaw > 0 ? cropRatioRaw : 1.5;
    const autoplayEnabled = carousel.dataset.galleryAutoplay === 'true' && !reducedMotion.matches;
    const autoplayDelayRaw = Number.parseInt(carousel.dataset.galleryDelay ?? '5000', 10);
    const autoplayDelay = Number.isFinite(autoplayDelayRaw)
      ? Math.max(2000, Math.min(20000, autoplayDelayRaw))
      : 5000;

    let currentIndex = 0;
    let maxIndex = 0;
    let positions = [];
    let slideOffsets = [];
    let trackGap = 0;
    let averageSlideWidth = 0;
    let lastViewportWidth = 0;
    let autoplayTimerId = null;
    let isPaused = false;
    let pointerStartX = 0;
    let pointerCurrentX = 0;
    let isDragging = false;
    let wasPointerMoved = false;
    let dots = [];
    const dragIntentThreshold = 10;

    const getVisibleSlides = () => {
      const viewportWidth = viewport.clientWidth;

      if (viewportWidth >= 1400) {
        return Math.min(3, slides.length);
      }

      if (viewportWidth >= 768) {
        return Math.min(2, slides.length);
      }

      return 1;
    };

    const getAverageAspectRatio = () => {
      const ratios = slides.map((slide) => {
        const image = slide.querySelector('img');

        if (!(image instanceof HTMLImageElement)) {
          return 0;
        }

        const width = image.naturalWidth || Number.parseFloat(image.getAttribute('width') ?? '0');
        const height = image.naturalHeight || Number.parseFloat(image.getAttribute('height') ?? '0');

        return width > 0 && height > 0 ? width / height : 0;
      }).filter((ratio) => ratio > 0);

      if (ratios.length === 0) {
        return 1.5;
      }

      return ratios.reduce((sum, ratio) => sum + ratio, 0) / ratios.length;
    };

    const setSlideA11y = () => {
      const currentPosition = positions[currentIndex] ?? 0;
      const viewportWidth = viewport.clientWidth;

      slides.forEach((slide, index) => {
        const slideStart = slideOffsets[index] ?? 0;
        const isVisible = slideStart >= currentPosition - 1
          && slideStart < currentPosition + viewportWidth;
        slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
      });
    };

    const setDotsState = () => {
      dots.forEach((dotButton, index) => {
        const isActive = index === currentIndex;
        dotButton.classList.toggle('is-active', isActive);
        dotButton.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    };

    const updateTransform = (offsetX = 0, immediate = false) => {
      if (immediate) {
        track.style.transition = 'none';
      }

      const translateX = -(positions[currentIndex] ?? 0) + offsetX;
      track.style.transform = `translate3d(${translateX}px, 0, 0)`;

      if (immediate) {
        window.requestAnimationFrame(() => {
          track.style.transition = '';
        });
      }
    };

    const rebuildDots = () => {
      if (!(dotsContainer instanceof HTMLElement)) {
        dots = [];
        return;
      }

      if (dots.length === maxIndex + 1) {
        return;
      }

      dotsContainer.innerHTML = '';
      dots = [];

      for (let index = 0; index <= maxIndex; index += 1) {
        const dotButton = document.createElement('button');
        dotButton.type = 'button';
        dotButton.className = 'showcase-carousel__dot gallery-carousel__dot';
        dotButton.setAttribute('aria-label', `Gehe zu Bild ${index + 1}`);
        dotButton.addEventListener('click', () => {
          goTo(index);
        });
        dotsContainer.appendChild(dotButton);
        dots.push(dotButton);
      }
    };

    const syncDimensions = () => {
      const viewportWidth = viewport.clientWidth;

      if (viewportWidth === 0) {
        return;
      }

      lastViewportWidth = viewportWidth;

      const trackStyles = window.getComputedStyle(track);
      trackGap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap || '0');
      if (!Number.isFinite(trackGap)) {
        trackGap = 0;
      }

      const visibleSlides = getVisibleSlides();
      const slideWidth = Math.max(
        1,
        (viewportWidth - (trackGap * visibleSlides)) / (visibleSlides + PEEK_RATIO)
      );
      const aspectRatio = isCropped ? cropRatio : getAverageAspectRatio();
      const slideHeight = Math.min(
        Math.max(slideWidth / aspectRatio, MIN_SLIDE_HEIGHT),
        Math.min(MAX_SLIDE_HEIGHT, window.innerHeight * 0.75)
      );

      carousel.style.setProperty('--gallery-height', `${slideHeight}px`);
      carousel.style.setProperty('--gallery-slide-width', isCropped ? `${slideWidth}px` : 'auto');

      const firstOffset = slides[0].offsetLeft;
      slideOffsets = slides.map((slide) => slide.offsetLeft - firstOffset);
      const lastSlide = slides[slides.length - 1];
      const contentWidth = (slideOffsets[slideOffsets.length - 1] ?? 0) + lastSlide.offsetWidth;
      const maxScroll = Math.max(0, contentWidth - viewportWidth);

      averageSlideWidth = contentWidth / slides.length;

      maxIndex = slideOffsets.findIndex((position) => position >= maxScroll - 1);
      if (maxIndex < 0) {
        maxIndex = slides.length - 1;
      }

      positions = slideOffsets
        .slice(0, maxIndex + 1)
        .map((position) => Math.min(position, maxScroll));

      if (currentIndex > maxIndex) {
        currentIndex = maxIndex;
      }

      rebuildDots();
      updateTransform(0, true);
      setSlideA11y();
      setDotsState();
    };

    const goTo = (index) => {
      const total = maxIndex + 1;
      currentIndex = ((index % total) + total) % total;

      updateTransform();
      setSlideA11y();
      setDotsState();
    };

    const stopAutoplay = () => {
      if (autoplayTimerId !== null) {
        window.clearInterval(autoplayTimerId);
        autoplayTimerId = null;
      }
    };

    const startAutoplay = () => {
      if (!autoplayEnabled || isPaused || autoplayTimerId !== null) {
        return;
      }

      autoplayTimerId = window.setInterval(() => {
        goTo(currentIndex + 1);
      }, autoplayDelay);
    };

    const pauseAutoplay = () => {
      isPaused = true;
      stopAutoplay();
    };

    const resumeAutoplay = () => {
      isPaused = false;
      startAutoplay();
    };

    const onPointerDown = (event) => {
      if (!event.isPrimary || event.button !== 0) {
        return;
      }

      if (event.target instanceof Element && event.target.closest(DRAG_INTERACTIVE_SELECTOR)) {
        return;
      }

      isDragging = true;
      wasPointerMoved = false;
      pointerStartX = event.clientX;
      pointerCurrentX = event.clientX;
      pauseAutoplay();
      track.style.transition = 'none';
      viewport.setPointerCapture(event.pointerId);
    };

    const onPointerMove = (event) => {
      if (!isDragging) {
        return;
      }

      pointerCurrentX = event.clientX;
      const deltaX = pointerCurrentX - pointerStartX;

      if (Math.abs(deltaX) > dragIntentThreshold) {
        wasPointerMoved = true;
      }

      if (wasPointerMoved) {
        updateTransform(deltaX, true);
      }
    };

    const onPointerUp = (event) => {
      if (!isDragging) {
        return;
      }

      isDragging = false;
      if (viewport.hasPointerCapture(event.pointerId)) {
        viewport.releasePointerCapture(event.pointerId);
      }
      track.style.transition = '';

      const deltaX = pointerCurrentX - pointerStartX;
      const threshold = Math.max(40, averageSlideWidth * 0.12);

      if (Math.abs(deltaX) >= threshold) {
        goTo(deltaX < 0 ? currentIndex + 1 : currentIndex - 1);
      } else {
        goTo(currentIndex);
      }

      resumeAutoplay();
    };

    prevButton?.addEventListener('click', () => {
      goTo(currentIndex - 1);
    });

    nextButton?.addEventListener('click', () => {
      goTo(currentIndex + 1);
    });

    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(currentIndex + 1);
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(currentIndex - 1);
      }
    });

    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove);
    viewport.addEventListener('pointerup', onPointerUp);
    viewport.addEventListener('pointercancel', onPointerUp);

    viewport.addEventListener('mouseenter', () => {
      if (!isDragging) {
        pauseAutoplay();
      }
    });
    viewport.addEventListener('mouseleave', () => {
      resumeAutoplay();
    });

    viewport.addEventListener('click', (event) => {
      if (wasPointerMoved) {
        event.preventDefault();
        event.stopPropagation();
      }

      wasPointerMoved = false;
    }, true);

    carousel.addEventListener('focusin', pauseAutoplay);
    carousel.addEventListener('focusout', (event) => {
      if (!(event.relatedTarget instanceof Node) || !carousel.contains(event.relatedTarget)) {
        resumeAutoplay();
      }
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stopAutoplay();
      } else {
        startAutoplay();
      }
    });

    // Galleries can sit inside accordions, so react to container width, not just window resize.
    if (typeof ResizeObserver === 'function') {
      const resizeObserver = new ResizeObserver(() => {
        if (viewport.clientWidth !== lastViewportWidth) {
          syncDimensions();
        }
      });
      resizeObserver.observe(viewport);
    } else {
      let resizeTimer = null;
      window.addEventListener('resize', () => {
        if (resizeTimer !== null) {
          window.clearTimeout(resizeTimer);
        }

        resizeTimer = window.setTimeout(() => {
          syncDimensions();
          resizeTimer = null;
        }, 100);
      });
    }

    slides.forEach((slide) => {
      const image = slide.querySelector('img');

      if (image instanceof HTMLImageElement && !image.complete) {
        image.addEventListener('load', syncDimensions, { once: true });
      }
    });

    syncDimensions();
    startAutoplay();
  });
}

function initMobileNavDrawer() {
  const toggle = document.getElementById('mobileNavToggle');
  const drawer = document.getElementById('mobileNavDrawer');

  if (!(toggle instanceof HTMLButtonElement) || !(drawer instanceof HTMLElement)) {
    return;
  }

  const desktopMediaQuery = window.matchMedia('(min-width: 768px)');

  function closeDrawer() {
    toggle.setAttribute('aria-expanded', 'false');
    drawer.hidden = true;
  }

  function openDrawer() {
    toggle.setAttribute('aria-expanded', 'true');
    drawer.hidden = false;
  }

  toggle.addEventListener('click', () => {
    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    if (isExpanded) {
      closeDrawer();
    } else {
      openDrawer();
    }
  });

  drawer.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    if (target.closest('a')) {
      closeDrawer();
    }
  });

  document.addEventListener('click', (event) => {
    if (drawer.hidden) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Node)) {
      return;
    }

    if (drawer.contains(target) || toggle.contains(target)) {
      return;
    }

    closeDrawer();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeDrawer();
    }
  });

  desktopMediaQuery.addEventListener('change', (event) => {
    if (event.matches) {
      closeDrawer();
    }
  });
}

function initNavSubmenus() {
  const navGroups = Array.from(document.querySelectorAll('.nav-group'));

  if (navGroups.length === 0) {
    return;
  }

  const desktopMediaQuery = window.matchMedia('(min-width: 768px)');
  const closeDelayMs = 180;

  navGroups.forEach((group) => {
    if (!(group instanceof HTMLDetailsElement)) {
      return;
    }

    let closeTimerId = null;

    group.addEventListener('mouseenter', () => {
      if (closeTimerId !== null) {
        window.clearTimeout(closeTimerId);
        closeTimerId = null;
      }

      if (desktopMediaQuery.matches) {
        group.open = true;
      }
    });

    group.addEventListener('mouseleave', () => {
      if (!desktopMediaQuery.matches) {
        return;
      }

      if (closeTimerId !== null) {
        window.clearTimeout(closeTimerId);
      }

      closeTimerId = window.setTimeout(() => {
        group.open = false;
        closeTimerId = null;
      }, closeDelayMs);
    });
  });
}

function initDarkMode() {
  // Check for saved theme preference or default to light mode
  const theme = localStorage.getItem('theme') || 'light';
  
  if (theme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
  
  // Listen for dark mode toggle events
  document.addEventListener('toggle-dark-mode', () => {
    const isDark = document.documentElement.classList.toggle('dark');
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  });
}

function initVeranstaltungenFilter() {
  const filterForm = document.getElementById('filterForm');
  const filterToolbar = document.getElementById('filterToolbar');
  const resetBtn = document.getElementById('resetFiltersBtn');
  const tableContainer = document.getElementById('veranstaltungenTableContainer');
  const activeFilterList = document.getElementById('activeFilterList');
  const typDropdownTitle = document.getElementById('typDropdownTitle');
  const zielgruppeDropdownTitle = document.getElementById('zielgruppeDropdownTitle');
  const filterInputs = Array.from(filterForm?.querySelectorAll('.filter-option-input') ?? []);
  const monthButtons = Array.from(filterForm?.querySelectorAll('.month-filter-button') ?? []);
  const filterApplyButtons = Array.from(filterForm?.querySelectorAll('[data-filter-apply]') ?? []);
  const filterResetButtons = Array.from(filterForm?.querySelectorAll('[data-filter-reset]') ?? []);
  const monthPrevBtn = document.getElementById('monthWindowPrev');
  const monthNextBtn = document.getElementById('monthWindowNext');
  const mobileMediaQuery = window.matchMedia('(max-width: 767px)');
  const hiddenInputs = {
    typ: document.getElementById('typInput'),
    zielpublikum: document.getElementById('zielpublikumInput'),
    month: document.getElementById('monthInput'),
  };

  let monthWindowStart = 0;

  function clearButtonFocus(button) {
    button.blur();

    // Some mobile browsers keep focus after tap until the next frame.
    window.requestAnimationFrame(() => {
      button.blur();
    });
  }

  function getOwningAccordionItem(triggerElement) {
    const directOwner = triggerElement.closest('.accordion-item');
    if (directOwner instanceof HTMLElement) {
      return directOwner;
    }

    const parentPanel = triggerElement.closest('.accordion-content');
    if (!(parentPanel instanceof HTMLElement)) {
      return null;
    }

    const ownerId = parentPanel.dataset.dropdownOwnerId;
    if (!ownerId) {
      return null;
    }

    const owner = document.getElementById(ownerId);
    return owner instanceof HTMLElement ? owner : null;
  }
  
  if (!filterForm || !tableContainer) {
    return; // Not on veranstaltungen page
  }

  filterInputs.forEach((input) => {
    input.addEventListener('change', async () => {
      syncHiddenInputs();
      renderActiveFilters();
      renderDropdownCounts();
      await applyFilters();
    });
  });

  filterApplyButtons.forEach((button) => {
    button.addEventListener('click', async () => {
      syncHiddenInputs();
      renderActiveFilters();
      renderDropdownCounts();
      await applyFilters();

      const accordionItem = getOwningAccordionItem(button);
      if (accordionItem) {
        accordionItem.classList.remove('is-open');
      }
    });
  });

  filterResetButtons.forEach((button) => {
    button.addEventListener('click', async () => {
      const accordionItem = getOwningAccordionItem(button);
      const panel = button.closest('.accordion-content');
      const scopedInputs = Array.from(
        panel?.querySelectorAll('.filter-option-input')
        ?? accordionItem?.querySelectorAll('.filter-option-input')
        ?? []
      );

      scopedInputs.forEach((input) => {
        if (input instanceof HTMLInputElement) {
          input.checked = false;
        }
      });

      syncHiddenInputs();
      renderActiveFilters();
      renderDropdownCounts();
      await applyFilters();

      if (accordionItem) {
        accordionItem.classList.remove('is-open');
      }
    });
  });

  monthButtons.forEach((button) => {
    button.addEventListener('click', async () => {
      if (!(hiddenInputs.month instanceof HTMLInputElement)) {
        return;
      }

      const monthValue = button.dataset.monthValue ?? '';
      const isDeselecting = hiddenInputs.month.value === monthValue;
      hiddenInputs.month.value = isDeselecting ? '' : monthValue;

      if (isDeselecting) {
        clearButtonFocus(button);
      }

      // Keep pending checkbox changes in sync when month buttons auto-apply filters.
      syncHiddenInputs();
      renderMonthButtons();
      renderActiveFilters();
      renderDropdownCounts();
      await applyFilters();
    });
  });

  if (monthPrevBtn instanceof HTMLButtonElement) {
    monthPrevBtn.addEventListener('click', () => {
      monthWindowStart -= 1;
      renderMonthButtons();
    });
  }

  if (monthNextBtn instanceof HTMLButtonElement) {
    monthNextBtn.addEventListener('click', () => {
      monthWindowStart += 1;
      renderMonthButtons();
    });
  }

  mobileMediaQuery.addEventListener('change', () => {
    syncMonthWindowToSelection();
    renderMonthButtons();
  });

  window.addEventListener('resize', () => {
    syncMonthWindowToSelection();
    renderMonthButtons();
  });

  if (filterToolbar instanceof HTMLElement && 'ResizeObserver' in window) {
    const toolbarObserver = new ResizeObserver(() => {
      syncMonthWindowToSelection();
      renderMonthButtons();
    });
    toolbarObserver.observe(filterToolbar);
  }
  
  // Handle reset button
  if (resetBtn) {
    resetBtn.addEventListener('click', async () => {
      filterInputs.forEach((input) => {
        input.checked = false;
      });

      if (hiddenInputs.month instanceof HTMLInputElement) {
        hiddenInputs.month.value = '';
      }

      syncHiddenInputs();
      syncMonthWindowToSelection();
      renderMonthButtons();
      renderActiveFilters();
      renderDropdownCounts();
      await applyFilters();

      // Remove focus so the reset button does not keep focus styling after click.
      resetBtn.blur();
    });
  }

  if (activeFilterList) {
    activeFilterList.addEventListener('click', async (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const trigger = event.target.closest('[data-remove-filter-type]');

      if (!trigger) {
        return;
      }

      const { removeFilterType, removeFilterValue } = trigger.dataset;
      if (removeFilterType === 'month') {
        if (hiddenInputs.month instanceof HTMLInputElement) {
          hiddenInputs.month.value = '';
          syncMonthWindowToSelection();
          renderMonthButtons();
        }
      } else {
        const input = filterForm.querySelector(
          `.filter-option-input[data-filter-type="${removeFilterType}"][data-filter-value="${removeFilterValue}"]`
        );

        if (!(input instanceof HTMLInputElement)) {
          return;
        }

        input.checked = false;
      }

      syncHiddenInputs();
      renderActiveFilters();
      renderDropdownCounts();
      await applyFilters();
    });
  }
  
  // Handle browser back/forward
  window.addEventListener('popstate', () => {
    location.reload();
  });

  syncHiddenInputs();
  renderActiveFilters();
  renderDropdownCounts();

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      syncMonthWindowToSelection();
      renderMonthButtons();
    });
  }

  function renderDropdownCounts() {
    const typCount = filterInputs.filter((input) => input.checked && input.dataset.filterType === 'typ').length;
    const zielgruppeCount = filterInputs.filter((input) => input.checked && input.dataset.filterType === 'zielpublikum').length;

    updateDropdownLabel(typDropdownTitle, typCount);
    updateDropdownLabel(zielgruppeDropdownTitle, zielgruppeCount);

    // Keep the current month window stable while still recalculating visibility.
    renderMonthButtons();
  }

  function updateDropdownLabel(labelNode, count) {
    if (!(labelNode instanceof HTMLElement)) {
      return;
    }

    const baseLabel = labelNode.dataset.baseLabel ?? labelNode.textContent ?? '';
    labelNode.textContent = count > 0 ? `${baseLabel} (${count})` : baseLabel;
  }

  function getSelectedFilters() {
    const selectedCheckboxFilters = filterInputs
      .filter((input) => input.checked)
      .map((input) => ({
        type: input.dataset.filterType,
        value: input.dataset.filterValue,
        label: input.dataset.filterLabel,
      }));

    if (hiddenInputs.month instanceof HTMLInputElement && hiddenInputs.month.value) {
      selectedCheckboxFilters.push({
        type: 'month',
        value: hiddenInputs.month.value,
        label: getMonthLabel(hiddenInputs.month.value),
      });
    }

    return selectedCheckboxFilters;
  }

  function syncHiddenInputs() {
    Object.entries(hiddenInputs).forEach(([filterType, hiddenInput]) => {
      if (!(hiddenInput instanceof HTMLInputElement)) {
        return;
      }

      if (filterType === 'month') {
        return;
      }

      const selectedValues = filterInputs
        .filter((input) => input.checked && input.dataset.filterType === filterType)
        .map((input) => input.dataset.filterValue);

      hiddenInput.value = selectedValues.join(',');
    });
  }

  function renderActiveFilters() {
    if (!activeFilterList) {
      return;
    }

    const selectedFilters = getSelectedFilters();
    const hasFilters = selectedFilters.length > 0;

    activeFilterList.innerHTML = '';
    activeFilterList.hidden = !hasFilters;

    selectedFilters.forEach((filter) => {
        const chip = document.createElement('button');
        chip.type = 'button';
      chip.className = 'btn-secondary filter-chip btn-sm';
        chip.dataset.removeFilterType = filter.type;
        chip.dataset.removeFilterValue = filter.value;

        const label = document.createElement('span');
        label.textContent = filter.label;

        const remove = document.createElement('span');
        remove.className = 'filter-chip-remove';
        remove.setAttribute('aria-hidden', 'true');
        remove.textContent = '×';

        chip.append(label, remove);
        activeFilterList.append(chip);
      });

    if (resetBtn instanceof HTMLButtonElement) {
      resetBtn.hidden = !hasFilters;
      resetBtn.disabled = !hasFilters;
      activeFilterList.append(resetBtn);
    }
  }

  function getMonthLabel(monthValue) {
    const selectedMonthButton = monthButtons.find((button) => button.dataset.monthValue === monthValue);
    return selectedMonthButton?.dataset.monthLabel ?? monthValue;
  }

  function getVisibleMonthCount() {
    const strip = document.getElementById('monthButtonStrip');
    if (!strip || monthButtons.length === 0) return 3;

    const stripWidth = strip.getBoundingClientRect().width;
    if (stripWidth === 0) return 3;

    const widths = monthButtons
      .map((button) => {
        const clone = /** @type {HTMLElement} */ (button.cloneNode(true));
        clone.hidden = false;
        clone.style.cssText = 'position:absolute;visibility:hidden;flex:none;width:max-content;min-width:0;pointer-events:none;top:-9999px;left:-9999px';
        document.body.appendChild(clone);
        const width = clone.getBoundingClientRect().width;
        document.body.removeChild(clone);
        return width;
      })
      .filter((width) => width > 0);

    if (widths.length === 0) return 3;

    const widestButtonWidth = Math.max(...widths);
    const stripStyles = getComputedStyle(strip);
    const gap = parseFloat(stripStyles.gap) || parseFloat(stripStyles.columnGap) || 8;
    const visibleCount = Math.floor((stripWidth + gap) / (widestButtonWidth + gap));
    return Math.max(1, Math.min(monthButtons.length, visibleCount));
  }

  function syncMonthWindowToSelection() {
    if (!(hiddenInputs.month instanceof HTMLInputElement)) {
      monthWindowStart = 0;
      return;
    }

    const selectedValue = hiddenInputs.month.value;
    const selectedIndex = monthButtons.findIndex((button) => button.dataset.monthValue === selectedValue);
    const visibleCount = getVisibleMonthCount();
    const maxStart = Math.max(0, monthButtons.length - visibleCount);

    if (selectedIndex < 0) {
      monthWindowStart = Math.min(monthWindowStart, maxStart);
      return;
    }

    const centeredStart = selectedIndex - Math.floor(visibleCount / 2);
    monthWindowStart = Math.min(Math.max(0, centeredStart), maxStart);
  }

  function renderMonthButtons() {
    const visibleCount = getVisibleMonthCount();
    const maxStart = Math.max(0, monthButtons.length - visibleCount);
    const selectedValue = hiddenInputs.month instanceof HTMLInputElement ? hiddenInputs.month.value : '';

    monthWindowStart = Math.min(Math.max(0, monthWindowStart), maxStart);

    monthButtons.forEach((button) => {
      const monthIndex = Number(button.dataset.monthIndex ?? '0');
      const isVisible = monthIndex >= monthWindowStart && monthIndex < monthWindowStart + visibleCount;
      const isActive = selectedValue !== '' && button.dataset.monthValue === selectedValue;

      button.hidden = !isVisible;
      button.classList.toggle('is-active', isActive);
      button.classList.toggle('btn-primary', isActive);
      button.classList.toggle('btn-secondary', !isActive);
    });

    if (monthPrevBtn instanceof HTMLButtonElement) {
      monthPrevBtn.disabled = monthWindowStart <= 0;
    }

    if (monthNextBtn instanceof HTMLButtonElement) {
      monthNextBtn.disabled = monthWindowStart >= maxStart;
    }
  }

  async function applyFilters() {
    const formData = new FormData(filterForm);
    const params = new URLSearchParams();
    
    // Add non-empty values
    for (const [key, value] of formData.entries()) {
      if (value) {
        params.append(key, value);
      }
    }
    params.append('ajax', '1');
    
    await loadTableData(params, tableContainer);
    
    // Update URL without page reload
    const newParams = new URLSearchParams();
    for (const [key, value] of formData.entries()) {
      if (value) {
        newParams.append(key, value);
      }
    }
    const newUrl = newParams.toString() 
      ? `${filterForm.action}?${newParams}` 
      : filterForm.action;
    history.pushState({}, '', newUrl);
  }
}

function initTableRowClicks() {
  const tableRows = document.querySelectorAll('tr[data-url]');
  
  tableRows.forEach(row => {
    row.addEventListener('click', (e) => {
      const url = row.dataset.url;
      const isExternal = row.dataset.external === 'true';
      const isNewTab = row.dataset.newTab === 'true';
      
      if (url) {
        if (isExternal || isNewTab) {
          window.open(url, '_blank', 'noopener,noreferrer');
        } else {
          window.location.href = url;
        }
      }
    });
  });
}

function initVeranstaltungenTableLoadMore(scope = document) {
  const wrappers = Array.from(scope.querySelectorAll('[data-vt-load-more-wrapper]'));

  wrappers.forEach((wrapper) => {
    const eventRows = Array.from(wrapper.querySelectorAll('tr[data-vt-event-row="1"]'));
    const loadMoreButton = wrapper.querySelector('[data-vt-load-more-button]');

    if (!(loadMoreButton instanceof HTMLButtonElement) || eventRows.length === 0) {
      return;
    }

    const initialVisible = Number(wrapper.dataset.vtInitialVisible ?? '0');
    const loadStep = Number(wrapper.dataset.vtLoadStep ?? '0');
    const safeStep = Number.isFinite(loadStep) && loadStep > 0 ? Math.floor(loadStep) : eventRows.length;

    let visibleCount = eventRows.filter((row) => !row.hidden).length;
    if (Number.isFinite(initialVisible) && initialVisible > 0) {
      visibleCount = Math.min(eventRows.length, Math.max(1, Math.floor(initialVisible)));
    }

    const syncYearDividerVisibility = () => {
      const rows = Array.from(wrapper.querySelectorAll('tbody > tr'));
      let currentDivider = null;
      let hasVisibleEventInSection = false;

      rows.forEach((row) => {
        if (row.classList.contains('vt-year-divider-row')) {
          if (currentDivider) {
            currentDivider.hidden = !hasVisibleEventInSection;
          }

          currentDivider = row;
          hasVisibleEventInSection = false;
          return;
        }

        if (row.dataset.vtEventRow === '1' && !row.hidden) {
          hasVisibleEventInSection = true;
        }
      });

      if (currentDivider) {
        currentDivider.hidden = !hasVisibleEventInSection;
      }
    };

    const render = () => {
      eventRows.forEach((row, index) => {
        row.hidden = index >= visibleCount;
      });

      syncYearDividerVisibility();
      loadMoreButton.hidden = visibleCount >= eventRows.length;
    };

    loadMoreButton.addEventListener('click', () => {
      visibleCount = Math.min(eventRows.length, visibleCount + safeStep);
      render();
    });

    render();
  });
}

async function loadTableData(params, container) {
  try {
    // Add loading state
    container.style.opacity = '0.5';
    container.style.pointerEvents = 'none';
    
    const response = await fetch(`${window.location.pathname}?${params}`);
    
    if (!response.ok) {
      throw new Error('Failed to load data');
    }
    
    const data = await response.json();
    
    // Update table content
    container.innerHTML = data.html;
    
    // Reinitialize row click handlers
    initTableRowClicks();
    initVeranstaltungenTableLoadMore(container);
    requestMobileRowsResync();
    
    // Remove loading state
    container.style.opacity = '1';
    container.style.pointerEvents = 'auto';

    return data;
  } catch (error) {
    console.error('Error loading table data:', error);
    
    // Remove loading state
    container.style.opacity = '1';
    container.style.pointerEvents = 'auto';
    
    // Show error message
    alert('Fehler beim Laden der Daten. Bitte versuchen Sie es erneut.');

    return null;
  }
}

// Export utilities for use in other modules
export function toggleDarkMode() {
  document.dispatchEvent(new Event('toggle-dark-mode'));
}

function initAccordion() {
  // Select all accordion headers (both accordion blocks and filter panel)
  const accordion_headers = document.querySelectorAll('.accordion-header');
  const accordion_close_buttons = document.querySelectorAll('.btn-filter-close');
  const dropdown_panel_host = document.getElementById('filterDropdownPanels');
  const desktop_filter_media_query = window.matchMedia('(min-width: 768px)');
  const dropdown_panel_records = [];

  const closeAccordionItem = (accordionItem) => {
    if (!(accordionItem instanceof HTMLElement)) {
      return;
    }

    accordionItem.classList.remove('is-open', 'is-opening', 'is-closing');
    requestMobileRowsResync();
  };

  const openAccordionItem = (accordionItem) => {
    if (!(accordionItem instanceof HTMLElement)) {
      return;
    }

    accordionItem.classList.remove('is-opening', 'is-closing');
    accordionItem.classList.add('is-open');
    requestMobileRowsResync();
  };

  if (dropdown_panel_host instanceof HTMLElement) {
    const dropdown_items = document.querySelectorAll('.accordion-item.filter-dropdown');

    dropdown_items.forEach((item) => {
      const header = item.querySelector('.accordion-header');
      const content = item.querySelector('.accordion-content');

      if (!(header instanceof HTMLElement) || !(content instanceof HTMLElement)) {
        return;
      }

      if (item.id) {
        content.dataset.dropdownOwnerId = item.id;
      }

      const anchor = document.createElement('div');
      anchor.hidden = true;
      header.insertAdjacentElement('afterend', anchor);

      dropdown_panel_records.push({ item, content, anchor });
    });

    const sync_dropdown_panel_placement = () => {
      dropdown_panel_records.forEach(({ content, anchor }) => {
        if (desktop_filter_media_query.matches) {
          dropdown_panel_host.appendChild(content);
        } else {
          anchor.insertAdjacentElement('beforebegin', content);
          content.classList.remove('is-open');
        }
      });
    };

    const sync_dropdown_panel_visibility = () => {
      if (!desktop_filter_media_query.matches) {
        return;
      }

      dropdown_panel_records.forEach(({ item, content }) => {
        content.classList.toggle('is-open', item.classList.contains('is-open'));
      });
    };

    sync_dropdown_panel_placement();
    sync_dropdown_panel_visibility();

    desktop_filter_media_query.addEventListener('change', () => {
      sync_dropdown_panel_placement();
      sync_dropdown_panel_visibility();
    });

    if ('MutationObserver' in window) {
      const observer = new MutationObserver(() => {
        sync_dropdown_panel_visibility();
      });

      dropdown_panel_records.forEach(({ item }) => {
        observer.observe(item, { attributes: true, attributeFilter: ['class'] });
      });
    }
  }
  
  accordion_headers.forEach(header => {
    header.addEventListener('click', () => {
      // Find the parent accordion-item
      const accordion_item = header.closest('.accordion-item');
      
      if (!accordion_item) {
        return;
      }
      
      const willOpen = !accordion_item.classList.contains('is-open');

      if (accordion_item.classList.contains('filter-dropdown') && willOpen) {
        document.querySelectorAll('.accordion-item.filter-dropdown.is-open').forEach((openItem) => {
          if (openItem !== accordion_item) {
            closeAccordionItem(openItem);
          }
        });
      }

      // Toggle the is-open class
      if (willOpen) {
        openAccordionItem(accordion_item);
      } else {
        closeAccordionItem(accordion_item);
      }
    });
  });

  accordion_close_buttons.forEach((button) => {
    button.addEventListener('click', () => {
      const accordion_item = button.closest('.accordion-item');

      if (!accordion_item) {
        return;
      }

      closeAccordionItem(accordion_item);
    });
  });

  const openAccordionByHash = () => {
    const hash = window.location.hash;
    if (!hash) return;

    const id = hash.slice(1);
    if (!id) return;
    const target = document.getElementById(id);
    if (!(target instanceof HTMLElement) || target.classList.contains('filter-dropdown')) return;

    openAccordionItem(target);

    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  openAccordionByHash();
  window.addEventListener('hashchange', openAccordionByHash);
}

function initTitleAppearanceOnScroll() {
  const titles = Array.from(document.querySelectorAll('.title-autoscale'));
  if (titles.length === 0) return;

  const triggerAnimation = (target) => {
    target.classList.remove('is-title-animating');
    void target.getBoundingClientRect(); // force reflow
    target.classList.add('is-title-animating');
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      triggerAnimation(entry.target);
    });
  }, { threshold: 0.7 });

  titles.forEach((title) => observer.observe(title));
}

function initMobileTableRowAnimations() {
  const mobileQuery = window.matchMedia('(max-width: 767px)');
  if (!mobileQuery.matches) return;

  if (mobileTableRowAnimationsBound) {
    requestMobileRowsResync();
    return;
  }
  mobileTableRowAnimationsBound = true;

  const triggerRow = (row) => {
    row.classList.remove('is-vt-row-animating');
    void row.getBoundingClientRect();
    row.classList.add('is-vt-row-animating');
  };

  // Track which rows currently have their top inside the band, to avoid re-firing.
  const rowsInBand = new Set();

  const checkRows = () => {
    if (!mobileQuery.matches) {
      rowsInBand.clear();
      return;
    }

    const rows = Array.from(document.querySelectorAll('#veranstaltungen-table tr.vt-event-row'));
    const currentRows = new Set(rows);

    rowsInBand.forEach((row) => {
      if (!currentRows.has(row)) {
        rowsInBand.delete(row);
      }
    });

    const vh = window.innerHeight;
    const bandTop = vh * 0.20;
    const bandBottom = vh * 0.30;

    rows.forEach((row) => {
      const { top } = row.getBoundingClientRect();
      if (top >= bandTop && top <= bandBottom) {
        if (!rowsInBand.has(row)) {
          rowsInBand.add(row);
          triggerRow(row);
        }
      } else {
        rowsInBand.delete(row);
      }
    });
  };

  const scheduleCheck = () => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(checkRows);
    });
  };

  window.addEventListener('scroll', checkRows, { passive: true });
  window.addEventListener('resize', scheduleCheck);
  mobileQuery.addEventListener('change', scheduleCheck);
  document.addEventListener(VT_MOBILE_ROWS_RESYNC_EVENT, () => {
    rowsInBand.clear();
    scheduleCheck();
  });

  scheduleCheck();
}


